import type { Alert, Vessel } from '@/types';

type Listener<T> = (event: T) => void;

/**
 * Periodic event simulator. Emits ticks of synthetic events to give the UI
 * the feeling of a live operational picture without external dependencies.
 *
 * Two streams:
 *   - alert_tick: occasionally surfaces a new low-severity alert
 *   - vessel_tick: advances each vessel's position along its course
 */
export class FeedSimulator {
  private alertListeners: Listener<Alert>[] = [];
  private vesselListeners: Listener<Vessel[]>[] = [];
  private clockListeners: Listener<Date>[] = [];
  private interval?: number;
  private vessels: Vessel[] = [];
  private tickCount = 0;

  start(initialVessels: Vessel[]) {
    this.vessels = initialVessels.map((v) => ({ ...v }));
    if (this.interval) return;
    this.interval = window.setInterval(() => this.tick(), 1500);
  }

  stop() {
    if (this.interval) {
      window.clearInterval(this.interval);
      this.interval = undefined;
    }
  }

  onAlert(fn: Listener<Alert>) {
    this.alertListeners.push(fn);
    return () => {
      this.alertListeners = this.alertListeners.filter((l) => l !== fn);
    };
  }

  onVessels(fn: Listener<Vessel[]>) {
    this.vesselListeners.push(fn);
    return () => {
      this.vesselListeners = this.vesselListeners.filter((l) => l !== fn);
    };
  }

  onClock(fn: Listener<Date>) {
    this.clockListeners.push(fn);
    return () => {
      this.clockListeners = this.clockListeners.filter((l) => l !== fn);
    };
  }

  private tick() {
    this.tickCount += 1;
    // Advance vessel positions
    for (const v of this.vessels) {
      if (v.ais_status === 'gap' || v.ais_status === 'dark') continue;
      const dt = 1500 / 1000; // seconds since last tick
      const speed_ms = (v.speed_knots * 1852) / 3600;
      const dist_m = speed_ms * dt;
      const dlat = (dist_m / 111111) * Math.cos((v.course * Math.PI) / 180);
      const dlon =
        (dist_m / (111111 * Math.cos((v.lat * Math.PI) / 180))) *
        Math.sin((v.course * Math.PI) / 180);
      v.lat += dlat;
      v.lon += dlon;
      v.last_seen = new Date().toISOString();
    }
    for (const fn of this.vesselListeners) fn(this.vessels);

    // Periodically emit a synthetic informational alert
    if (this.tickCount % 18 === 0) {
      const generated = synthesiseAlert();
      for (const fn of this.alertListeners) fn(generated);
    }

    // Clock tick
    const now = new Date();
    for (const fn of this.clockListeners) fn(now);
  }
}

const synthAlertTitles = [
  'AIS gap detected on cargo vessel transiting Baltic SAR coverage',
  'Anomalous DNS resolution attempt in DMZ - corp-update.cn',
  'Vendor remote access connection - verifying maintenance window',
  'Sentinel-1 SAR delta detected over offshore wind corridor - investigation queued',
  'GOOSE message latency increase on substation process bus - within tolerance',
  'Geofence breach (low confidence) - UAS-class radar contact, ID pending',
];

const synthAssets = [
  'AST-NL-EEMS-DC1',
  'AST-EE-VIRU-SUB',
  'AST-DE-DOLE-SUB',
  'AST-XX-BALT-PIP',
  'AST-PL-SWNS-LNG',
];

let synthCounter = 0;

function synthesiseAlert(): Alert {
  synthCounter += 1;
  const title =
    synthAlertTitles[synthCounter % synthAlertTitles.length] ?? 'Synthetic alert';
  const asset = synthAssets[synthCounter % synthAssets.length] ?? 'AST-NL-EEMS-DC1';
  return {
    alert_id: `ALR-LIVE-${Date.now()}-${synthCounter}`,
    title,
    description:
      'Synthetic informational event from live feed simulator (Iris does not generate real OT/cyber decisions in demo mode).',
    severity: 2,
    status: 'open',
    category: 'cyber',
    asset_ids: [asset],
    detected_at: new Date().toISOString(),
    confidence: 70,
    evidence: [],
    recommended_actions: [],
  };
}
